/**
 * PromptLibraryComponent
 * 
 * This Angular component displays a prompt library containing static and RegRadar-based
 * questions based on a selected dataset/module. It supports language localization,
 * question copy functionality, and emits events to integrate with a chatbot system.
 * 
 * Features:
 * - Displays top N RegRadar and static questions from backend RegRadar API
 * - Supports translation using ngx-translate
 * - Allows dataset switching and question selection
 * - Emits events to parent components for integration
 */

import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { SelectedModuleService } from '../../../services/selected-module-service/selected-module.service';
import { LanguageChangeService } from 'src/services/language-change/language-change.service';
import { TranslateService } from '@ngx-translate/core';
import { ChatbotService } from 'src/services/chatbot-services/chatbot.service';

import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { LoggerService } from '../../../services/logger-services/logger.service';
import { RegRadarResponse, Question, AppConfigResponse } from '../../models/prompt-library.models';
import agentConfig from '../../../config/agent_config.json';

@Component({
  selector: 'app-prompt-library',
  templateUrl: './prompt-library.component.html',
  styleUrls: ['./prompt-library.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromptLibraryComponent implements OnInit, OnChanges {

  // ============================
  //          @Input()
  // ============================

  /**
   * Dataset currently toggled for filtering questions.
   */
  @Input() toggledDbDataset: any;

  /**
   * Currently selected dataset in the chatbot session.
   */
  @Input() selectedVectorDbDataset: any;

  /**
   * List of available vector DB datasets for module switching.
   */
  @Input() vectorDbDatasets: any;

  /**
   * Flag indicating whether the chatbot loader/spinner is active.
   */
  @Input() loaderShow: boolean = false;

  /**
   * Flag to indicate whether response streaming is ongoing.
   */
  @Input() IsResponseStreaming: boolean = false;

  /**
   * Active domain name (e.g., 'regulation') for filtering data.
   */
  @Input() domainName: string = '';

  /**
   * Tenant ID used in backend API call.
   */
  @Input() tenantId: any;

  /**
   * Domain ID used in backend API call.
   */
  @Input() userId: any;

  /**
   * Optional. When true, indicates the prompt library is used in agent context.
   * Default: false.
   */
  @Input() isAgent: boolean = false;

  @Input() isRegRader : boolean = true
  @Input() isRegSelect : boolean = true

  /**
   * Optional. Name of the agent when used in agent context.
   * Default: ''.
   */
  @Input() agentName: string = '';


  // ============================
  //          @Output()
  // ============================

  /**
   * Emits when a new dataset is selected (switch module).
   */
  @Output() newSelectedDataset = new EventEmitter<any>();

  /**
   * Emits when the chatbot should send a request (false = auto-send).
   */
  @Output() sendRequestEmitted = new EventEmitter<boolean>();

  /**
   * Emits when a new chat session is to be started.
   */
  @Output() clickNewChatFunc = new EventEmitter<boolean>();

  /**
   * Emits the selected question to be sent to the chatbot.
   */
  @Output() uploadQuestionSelected = new EventEmitter<string>();

  /**
   * Emits when the popup dialog is closed.
   */
  @Output() close = new EventEmitter<void>();

  /**
   * Emits to trigger scrolling the chat window to the bottom.
   */
  @Output() scrollBottom = new EventEmitter<void>();


  // ============================
  //       Internal State
  // ============================

  filterQuestionsDictionary: RegRadarResponse = { regRadar: [], static: [] };
  hoveredIndex = -1;
  copiedIndex = -1;
  isLoading = false;

  regRadarQuestionCount = 5;
  staticQuestionCount = 5;

  // Tour state - Prompt Library Tour
  showTourCard = true;
  tourStep = 1;
  totalSteps = 2; // Example: RegRadar first, Static second
  tourCardData: { title: string; description: string } = { title: '', description: '' };
  tourCardPosition: { top: number; left: number } = { top: 0, left: 0 };
  highlightedQuestion: { type: 'regRadar' | 'static'; index: number } | null = null;

  /** Error template for PROMPT LIBRARY */
  private readonly promptLibraryError = (error: any) => 
    `[PROMPT LIBRARY] - ${JSON.stringify(error)}`;



  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly sharedService: SelectedModuleService,
    private readonly languageService: LanguageChangeService,
    private readonly translate: TranslateService,
    private readonly chatbotService: ChatbotService,
    private readonly loggerService: LoggerService
  ) {}

  /** Maps agent id to the static-questions array key in agent_config.json. */
  private readonly agentQuestionsConfigKey: Record<string, 'VIA_Agent_Questions' | 'RIA_Agent_Questions'> = {
    via_agent: 'VIA_Agent_Questions',
    ria_agent: 'RIA_Agent_Questions',
  };

  /**
   * True when questions should be loaded from agent_config.json instead of API.
   * Use API when agentName is empty or when agentName is "regulations_agent" (id or display).
   */
  private get useConfigQuestions(): boolean {
    if (!this.agentName || this.agentName.trim() === '') return false;
    return this.normalizeAgentName(this.agentName) !== 'regulations_agent';
  }

  private normalizeAgentName(name: string): string {
    return (name || '').toLowerCase().trim().replace(/\s+/g, '_');
  }

  /** True when Reg Radar / Expert Curated headers and icons should be shown (agentName empty or regulations_agent). */
  get showRegRadarStaticHeaders(): boolean {
    return !this.useConfigQuestions;
  }

  /** Cache key for last API/config load to avoid duplicate calls when inputs re-bind with same values. */
  private lastLoadKey: string = '';

  /** Angular lifecycle hook called on component init. */
  ngOnInit(): void {
    this.loadQuestions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const agentChanged = changes['agentName'] && !changes['agentName'].firstChange;
    const datasetChanged = changes['toggledDbDataset'] && !changes['toggledDbDataset'].firstChange;
    if (agentChanged || datasetChanged) {
      this.loadQuestions();
    }
  }

  /**
   * Loads questions either from config (VIA_Agent_Questions) or from API based on agentName.
   * Skips if already loading or if the same load (agent + dataset) was just requested.
   */
  private loadQuestions(): void {
    if (this.isLoading) return;

    const loadKey = `${this.agentName ?? ''}|${this.toggledDbDataset?.name ?? ''}`;
    if (loadKey === this.lastLoadKey) return;
    this.lastLoadKey = loadKey;

    if (this.useConfigQuestions) {
      this.loadQuestionsFromConfig();
    } else if (this.toggledDbDataset?.name != null) {
      this.updateQuestionsForDataset(this.toggledDbDataset);
    } else {
      this.lastLoadKey = '';
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Loads static questions from src/config/agent_config.json for the active agent
   * (via_agent.VIA_Agent_Questions or ria_agent.RIA_Agent_Questions).
   * Populates static list only; regRadar stays empty.
   */
  private loadQuestionsFromConfig(): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    try {
      const list = this.getAgentStaticQuestionsFromConfig();
      const staticQuestions: Question[] = list.map(text => ({
        DomainName: '',
        Module: this.domainName,
        Question: text,
        QuestionJP: text,
        QuestionCN: text
      }));
      this.filterQuestionsDictionary = { regRadar: [], static: staticQuestions };
    } catch (err) {
      this.loggerService.logError(this.promptLibraryError(err), 'loadQuestionsFromConfig');
      this.filterQuestionsDictionary = { regRadar: [], static: [] };
    }
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  /**
   * Resolves static question translation keys for the current agent from agent_config.json.
   */
  private getAgentStaticQuestionsFromConfig(): string[] {
    const agentId = this.normalizeAgentName(this.agentName);
    const questionsKey = this.agentQuestionsConfigKey[agentId];
    if (!questionsKey) {
      return [];
    }

    const agentEntry = (agentConfig as { agents?: Array<Record<string, unknown>> })?.agents?.find(
      a => a['id'] === agentId
    );
    const raw = agentEntry?.[questionsKey];
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw.filter((q): q is string => typeof q === 'string' && q.trim() !== '');
  }

  /**
   * Fetches RegRadar and static questions from backend.
   *
   * @param dataset - Selected dataset/module
   * @returns Observable of formatted RegRadarResponse
   */
  getRegRadarStaticQuestions(dataset: any): Observable<RegRadarResponse> {
    
    return this.callRegRadarAPI(dataset).pipe(
      map((response: AppConfigResponse) => {
        const regRadar: Question[] = [];
        const staticQuestions: Question[] = [];
        
        // Add null/undefined checks for app_config_data (which is directly an array)
        if (response.app_config_data && Array.isArray(response.app_config_data)) {
          staticQuestions.push(
            ...response.app_config_data.slice(0, this.staticQuestionCount).map(q => ({
              DomainName: dataset.value,
              Module: this.domainName,
              Question: q.english || '',
              QuestionJP: q.japanese || '',
              QuestionCN: q.chinese || ''
            }))
          );
        }
        
  
        // Add null/undefined checks for reg_radar_data (which is directly an array)
        if (response.reg_radar_data && Array.isArray(response.reg_radar_data)) {
          regRadar.push(
            ...response.reg_radar_data.slice(0, this.regRadarQuestionCount).map(q => ({
              DomainName: dataset.value,
              Module: this.domainName,
              Question: q.english || '',
              QuestionJP: q.japanese || '',
              QuestionCN: q.chinese || ''
            }))
          );
        }
  
        // Filter out entries where selected language text is empty
        const lang = this.languageService.getCurrentLanguage();
        
        const filterFn = (q: Question) => {
          if (lang === 'en') return !!q.Question.trim();
          if (lang === 'jp') return !!q.QuestionJP.trim();
          if (lang === 'zhCN') return !!q.QuestionCN.trim();
          return false;
        };
  
        const filteredRegRadar = regRadar.filter(filterFn);
        const filteredStaticQuestions = staticQuestions.filter(filterFn);
    
        return { regRadar: filteredRegRadar, static: filteredStaticQuestions };
      }),
      catchError(error => {
        this.loggerService.logError(this.promptLibraryError(error), "getRegRadarStaticQuestions");
        return of({ regRadar: [], static: [] });
      })
    );
  }
  

  /**
   * Updates the local questions dictionary for the selected dataset.
   *
   * @param dataset - The dataset selected
   */
  updateQuestionsForDataset(dataset: any): void {
    this.isLoading = true;
    this.cdr.markForCheck();
    try {
      this.getRegRadarStaticQuestions(dataset).subscribe({
        next: (data) => {
          this.filterQuestionsDictionary = data;
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.lastLoadKey = '';
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
    } catch (error) {
      this.lastLoadKey = '';
      this.loggerService.logError(this.promptLibraryError(error), "updateQuestionsForDataset");
    }
  }

  /**
   * Copies a selected question to clipboard and shows feedback for 2s.
   *
   * @param text - Question text (may be a translation key)
   * @param index - Index for feedback state
   */
  copyQuestion(text: string, index: number): void {
    // Translate the question if it's a translation key
    const translatedText = this.translate.instant(text.trim());
    navigator.clipboard.writeText(translatedText).then(() => {      this.copiedIndex = index;
      this.cdr.markForCheck();
      setTimeout(() => {
        this.copiedIndex = -1;
        this.cdr.markForCheck();
      }, 2000);
    }).catch((error) => {
      this.loggerService.logError(this.promptLibraryError(error), "copyQuestion");
    });
  }

  /**
   * Selects a dataset (module) and updates related question list.
   *
   * @param tag - Selected dataset tag
   */
  selectTag(tag: any): void {
    this.toggledDbDataset = tag;
    this.lastLoadKey = `${this.agentName ?? ''}|${tag?.name ?? ''}`;
    this.updateQuestionsForDataset(tag);
  }

  /**
   * Handles question selection: emits events to update chatbot session.
   *
   * @param question - The selected question text (may be a translation key)
   */
  uploadQuestion(question: string): void {
    try {
      // Translate the question if it's a translation key
      const translatedQuestion = this.translate.instant(question.trim());
      this.uploadQuestionSelected.emit(translatedQuestion);
      this.closePopup();

      if (this.isAgent) {
        // Agent context: only display the question in the text area; do not send to API
        this.cdr.markForCheck();
        return;
      }

      this.scrollBottom.emit();
      const selectedDataset = this.vectorDbDatasets.find((d: any) => d.value === this.toggledDbDataset.value) || this.toggledDbDataset;

      if (this.toggledDbDataset.value !== this.selectedVectorDbDataset.value) {
        this.clickNewChatFunc.emit(true);
        this.newSelectedDataset.emit(selectedDataset);
      }

      this.sharedService.changeSelectedValue({
        type: 1,
        selectedVectorDbDataset: selectedDataset
      });

      this.newSelectedDataset.emit(selectedDataset);

      if (!this.loaderShow) {
        this.sendRequestEmitted.emit(false);
      }

      this.cdr.markForCheck();
    } catch (error) {
      this.loggerService.logError(this.promptLibraryError(error), "uploadQuestion");
    }
  }

  /**
   * Closes the popup by emitting a `close` event.
   */
  closePopup(): void {
    this.close.emit();
  }

  /**
   * Gets current language code from language service (e.g., 'en', 'jp').
   */
  get currentLanguage(): string {
    return this.languageService.getCurrentLanguage();
  }

  /**
   * Checks if there are any questions available (RegRadar or static).
   */
  get hasQuestions(): boolean {
    return this.filterQuestionsDictionary.regRadar.length > 0 || 
           this.filterQuestionsDictionary.static.length > 0;
  }

  /**
   * Calls RegRadar API using chatbot service with necessary context.
   *
   * @param tag - Dataset/module for which questions are fetched
   * @returns Observable of AppConfigResponse
   */
  callRegRadarAPI(tag: any): Observable<AppConfigResponse> {
    if (!tag?.name) {
      this.loggerService.logError(this.promptLibraryError('Missing dataset: toggledDbDataset is undefined or has no name'), "callRegRadarAPI");
      return of({ app_config_data: [], reg_radar_data: [] });
    }
    const { tenantId, domainName, userId } = this;
    const moduleName = tag.name.toLowerCase();

    return this.chatbotService.getRegRadarAPI(userId, tenantId, domainName, moduleName).pipe(
      catchError(error => {
        this.loggerService.logError(this.promptLibraryError(error), "callRegRadarAPI");
        throw error;
      })
    );
  }
}
